// src/components/PaymentModal.js
// CARD  → billing shortcut (DummyCardProvider / demo)
// TAP   → real Tap Payments v2 API via /api/payments/*
// CASH  → billing shortcut + counter screen
// Other → billing shortcut

import React, { useState, useEffect, useRef } from 'react';
import api           from '../services/api';
import CashGateModal from './CashGateModal';

const DEFAULT_CONFIG = {
  CASH: true, CARD: true, UPI: true, WALLET: false, TAP: false, allowCashOnQR: false,
};

const ALL_GATEWAYS = [
  { id:'CARD',   label:'Credit / Debit Card', icon:'💳', desc:'Visa, Mastercard, Amex'   },
  { id:'CASH',   label:'Cash',                icon:'💵', desc:'Pay at the counter'        },
  { id:'UPI',    label:'UPI / QR Pay',        icon:'📱', desc:'Google Pay, PhonePe'      },
  { id:'WALLET', label:'Digital Wallet',      icon:'⚡', desc:'Apple Pay, Samsung Pay'   },
  { id:'TAP',    label:'Tap Payments',        icon:'🔵', desc:'UAE fast payment gateway' },
];

const TAP_POLL_INTERVAL = 3000;   // ms between status checks
const TAP_POLL_MAX      = 100;    // ~5 min timeout

const isQROrder = (src) => src === 'STALL_QR' || src === 'TABLE_QR';

function fmtCard(v)   { return v.replace(/\D/g,'').slice(0,16).replace(/(.{4})/g,'$1 ').trim(); }
function fmtExpiry(v) { const d=v.replace(/\D/g,'').slice(0,4); return d.length>=3?d.slice(0,2)+'/'+d.slice(2):d; }

export default function PaymentModal({
  order,
  vendorId,
  paymentConfig: propConfig,
  sourceKiosk = 'VENDOR_STALL',
  onSuccess,
  onClose,
}) {
  const [config,        setConfig]       = useState(DEFAULT_CONFIG);
  const [gateway,       setGateway]      = useState('CARD');
  const [step,          setStep]         = useState('select');
  const [card,          setCard]         = useState({ number:'', expiry:'', cvv:'', name:'' });
  const [error,         setError]        = useState('');
  const [loading,       setLoading]      = useState(false);
  const [showCashGate,  setShowCashGate] = useState(false);

  // TAP-specific state
  const [tapPaymentId,   setTapPaymentId]   = useState(null);
  const [tapRedirectUrl, setTapRedirectUrl] = useState(null);
  const pollTimerRef = useRef(null);
  const pollCountRef = useRef(0);

  const isQR  = isQROrder(sourceKiosk);
  const total = Number(order?.total || 0).toFixed(2);

  // Stop polling when component unmounts
  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  // Load vendor payment config from the correct public endpoint
  useEffect(() => {
    if (propConfig) {
      const cfg = typeof propConfig === 'string' ? JSON.parse(propConfig) : propConfig;
      setConfig({ ...DEFAULT_CONFIG, ...cfg });
      return;
    }
    const vid = vendorId || order?.vendorId;
    if (vid) {
      // GET /api/payment-config/public/:vendorId returns the config object directly
      api.get(`/payment-config/public/${vid}`)
        .then(r => { setConfig({ ...DEFAULT_CONFIG, ...r.data }); })
        .catch(() => {});
    }
  }, [vendorId, order?.vendorId, propConfig]);

  const availableGW = ALL_GATEWAYS.filter(g => {
    if (!config[g.id]) return false;
    if (g.id === 'CASH' && isQR && !config.allowCashOnQR) return false;
    return true;
  });

  useEffect(() => {
    if (!availableGW.find(g => g.id === gateway)) {
      setGateway(availableGW[0]?.id || 'CARD');
    }
  }, [config]); // eslint-disable-line

  // ── Validate card fields ──────────────────────────────────
  const validateCard = () => {
    const num = card.number.replace(/\s/g,'');
    if (num.length < 13)                          return 'Invalid card number';
    if (!card.expiry.match(/^\d{2}\/\d{2}$/))    return 'Invalid expiry (MM/YY)';
    if (card.cvv.length !== 3)                     return 'CVV must be 3 digits';
    if (!card.name.trim())                        return 'Cardholder name required';
    const [mm, yy] = card.expiry.split('/');
    if (new Date(2000 + parseInt(yy), parseInt(mm) - 1, 1) < new Date()) return 'Card has expired';
    return '';
  };

  const cardValid = () => validateCard() === '';

  // ── Billing shortcut (CARD demo / CASH / UPI / WALLET) ───
  const markPaid = async (paymentMode) => {
    // Demo/offline orders (id starts with 'demo-') — simulate success
    if (!order.id || order.id.startsWith('demo-')) {
      if (paymentMode === 'CASH') { setStep('cash-counter'); }
      else { setStep('success'); setTimeout(() => onSuccess({ success: true, gateway: paymentMode }), 1800); }
      return;
    }

    if (paymentMode !== 'CASH') {
      try { await api.post(`/billing/invoice/${order.id}`); } catch (_) {}
    }
    try {
      // For CASH this only tags the order (paymentMode=CASH) and returns
      // cashPending:true — it does NOT mark paid or notify the kitchen.
      // The order only reaches the kitchen once staff confirms cash was
      // received via POST /billing/confirm-cash/:orderId at the counter.
      await api.post(`/billing/pay/${order.id}`, { paymentMode });
      if (paymentMode === 'CASH') {
        setStep('cash-counter');
      } else {
        setStep('success');
        setTimeout(() => onSuccess({ success: true, gateway: paymentMode }), 1800);
      }
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Payment confirmation failed');
    }
  };

  // ── TAP Payments — real gateway call ─────────────────────
  const processTapPayment = async () => {
    const initRes = await api.post('/payments/initiate', {
      orderId:    order.id,
      amount:     parseFloat(total),
      currency:   'AED',
      gateway:    'TAP',
      cardNumber: card.number.replace(/\s/g,''),
      expiry:     card.expiry,
      cvv:        card.cvv,
      cardHolder: card.name,
    });

    const { paymentId, status, clientSecret } = initRes.data;

    if (clientSecret) {
      // Tap returned a 3DS / hosted-checkout URL — show as QR for customer's phone
      setTapPaymentId(paymentId);
      setTapRedirectUrl(clientSecret);
      setStep('tap-qr');
      setLoading(false);
      pollCountRef.current = 0;
      scheduleTapPoll(paymentId);
      return;
    }

    // Direct approval (no 3DS)
    if (status === 'approved' || status === 'pending') {
      const confirmRes = await api.post('/payments/confirm', { paymentId });
      if (!confirmRes.data.success) throw new Error('Tap confirmation failed');
      setStep('success');
      setTimeout(() => onSuccess({ success: true, gateway: 'TAP' }), 1800);
    } else {
      throw new Error(initRes.data.error || 'Tap payment declined');
    }
  };

  // ── TAP status polling ────────────────────────────────────
  const scheduleTapPoll = (paymentId) => {
    pollTimerRef.current = setTimeout(async () => {
      if (pollCountRef.current++ >= TAP_POLL_MAX) {
        setError('Payment timeout — please try again.');
        setStep('declined');
        return;
      }
      try {
        const res = await api.get(`/payments/status/${paymentId}`);
        if (res.data.status === 'PAID') {
          if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
          setStep('success');
          setTimeout(() => onSuccess({ success: true, gateway: 'TAP' }), 1800);
          return;
        }
        if (['FAILED','REFUNDED','CANCELLED'].includes(res.data.status)) {
          setError('Payment failed or cancelled. Please try again.');
          setStep('declined');
          return;
        }
      } catch (err) {
        // Stop polling on 404 (payment not found) or 401 (auth issue)
        const status = err.response?.status;
        if (status === 404 || status === 401) {
          setError('Payment session not found. Please try again.');
          setStep('declined');
          return;
        }
        // Network errors — continue polling
      }
      scheduleTapPoll(paymentId); // continue polling
    }, TAP_POLL_INTERVAL);
  };

  // ── Main payment dispatcher ───────────────────────────────
  const processPayment = async () => {
    if (!order?.id) { setError('No order found. Please try again.'); return; }

    if (gateway === 'CASH' && isQR) { setShowCashGate(true); return; }
    if (gateway === 'CASH')         { await markPaid('CASH'); return; }

    // Validate card for CARD and TAP
    if (gateway === 'CARD' || gateway === 'TAP') {
      const err = validateCard();
      if (err) { setError(err); return; }
    }

    setLoading(true); setError(''); setStep('processing');
    try {
      if (gateway === 'TAP') {
        await processTapPayment();
      } else {
        // CARD (demo), UPI, WALLET — billing shortcut
        await markPaid(gateway);
      }
    } catch (err) {
      setError(err.message || 'Payment failed');
      setStep('declined');
    } finally {
      if (step !== 'tap-qr') setLoading(false);
    }
  };

  const handleCashGateConfirm = async () => {
    setShowCashGate(false);
    await markPaid('CASH');
  };

  const handleCancelTap = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setTapPaymentId(null); setTapRedirectUrl(null);
    setStep('select');
  };

  // ── Derived header title ──────────────────────────────────
  const headerTitle = () => {
    switch (step) {
      case 'select':      return '💳 Select Payment';
      case 'card':        return gateway === 'TAP' ? '🔵 Tap Card Details' : '💳 Card Details';
      case 'cash-counter':return '💵 Pay at Counter';
      case 'processing':  return '⏳ Processing...';
      case 'tap-qr':      return '🔵 Tap Checkout';
      case 'success':     return '✅ Payment Complete';
      default:            return '❌ Payment Failed';
    }
  };

  const blockClose = ['processing','success','cash-counter','tap-qr'].includes(step);

  // ── Order summary sub-component ───────────────────────────
  const OrderSummary = () => (
    <div style={{ background:'var(--bg3)', borderRadius:10, padding:'12px 14px', marginBottom:20 }}>
      <div style={{ fontSize:11, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>
        Order {order?.orderNumber || ''}
        {order?.tableNo && <span style={{ marginLeft:8 }}>· 🪑 Table {order.tableNo}</span>}
      </div>
      {order?.items?.map((item, i) => (
        <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--text2)', marginBottom:3 }}>
          <span>{item.name} ×{item.quantity || item.qty}</span>
          <span>AED {((item.price||0) * (item.quantity||item.qty||1)).toFixed(2)}</span>
        </div>
      ))}
      {(order?.subtotal != null && order?.tax != null) && (
        <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid var(--border)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--text2)', marginBottom:4 }}>
            <span>Subtotal</span>
            <span>AED {Number(order.subtotal).toFixed(2)}</span>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--text2)' }}>
            <span>VAT (5%)</span>
            <span>AED {Number(order.tax).toFixed(2)}</span>
          </div>
          {(order?.serviceFee?.amount > 0 || order?.serviceFeeAmount > 0) && (
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--text2)', marginTop:4 }}>
              <span>{order?.serviceFee?.label || order?.serviceFeeLabel || 'Service Fee'}</span>
              <span>AED {Number(order?.serviceFee?.amount ?? order?.serviceFeeAmount ?? 0).toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
      <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:17, marginTop:10, paddingTop:10, borderTop:'1px solid var(--border)' }}>
        <span>Total</span>
        <span style={{ color:'var(--accent)' }}>AED {total}</span>
      </div>
    </div>
  );

  return (
    <>
      <div
        style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}
        onClick={e => { if (e.target === e.currentTarget && !blockClose) onClose(); }}>

        <div style={{ background:'var(--bg2)', borderRadius:20, width:'100%', maxWidth:460, border:'1px solid var(--border)', overflow:'hidden' }}>

          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 22px', borderBottom:'1px solid var(--border)' }}>
            <div style={{ fontWeight:700, fontSize:16 }}>{headerTitle()}</div>
            {!blockClose && (
              <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text3)', fontSize:20, cursor:'pointer' }}>✕</button>
            )}
          </div>

          <div style={{ padding:'20px 22px' }}>

            {/* ── SELECT ── */}
            {step === 'select' && (
              <>
                <OrderSummary />
                {isQR && config.allowCashOnQR && (
                  <div style={{ padding:'10px 14px', background:'#3d2e10', borderRadius:8, marginBottom:12, fontSize:12, color:'var(--accent)', border:'1px solid var(--accent)' }}>
                    💡 Cash orders require payment at the counter before preparation begins.
                  </div>
                )}
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
                  {availableGW.map(g => (
                    <div key={g.id} onClick={() => setGateway(g.id)}
                      style={{ display:'flex', alignItems:'center', gap:14, padding:'13px 16px', borderRadius:10, cursor:'pointer',
                        border:`2px solid ${gateway===g.id?'var(--accent)':'var(--border)'}`,
                        background:gateway===g.id?'#3d2e10':'var(--bg3)', transition:'all .15s' }}>
                      <span style={{ fontSize:26 }}>{g.icon}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:14, color:gateway===g.id?'var(--accent)':'var(--text)' }}>{g.label}</div>
                        <div style={{ fontSize:12, color:'var(--text3)' }}>{g.desc}</div>
                      </div>
                      <div style={{ width:20, height:20, borderRadius:'50%',
                        border:`2px solid ${gateway===g.id?'var(--accent)':'var(--border)'}`,
                        background:gateway===g.id?'var(--accent)':'transparent' }} />
                    </div>
                  ))}
                </div>
                <button className="btn btn-primary" style={{ width:'100%', padding:14, fontSize:15 }}
                  onClick={() => (gateway==='CARD' || gateway==='TAP') ? setStep('card') : processPayment()}>
                  {gateway==='CASH' ? '✓ Place Order & Pay at Counter' : 'Continue →'}
                </button>
              </>
            )}

            {/* ── CARD FORM (CARD + TAP) ── */}
            {step === 'card' && (
              <>
                <OrderSummary />
                {error && (
                  <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:14, background:'#3d1a1a', color:'var(--red)', fontSize:13 }}>❌ {error}</div>
                )}

                {/* Test-card hint differs per gateway */}
                {gateway === 'TAP' ? (
                  <div style={{ padding:'8px 12px', borderRadius:8, marginBottom:16, background:'#1a1a3d', fontSize:12, color:'#6080ff', border:'1px solid #2a2a6a' }}>
                    🔵 <strong>Tap Test:</strong> 4508 7500 1574 1019 · exp 01/39 · CVV 100
                  </div>
                ) : (
                  <div style={{ padding:'8px 12px', borderRadius:8, marginBottom:16, background:'#1a2d3d', fontSize:12, color:'var(--blue)' }}>
                    💡 <strong>Test:</strong> 4111 1111 1111 1111 · any future date · any 3-digit CVV
                  </div>
                )}

                <div style={{ marginBottom:14 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Card Number</div>
                  <input type="text" inputMode="numeric" placeholder="1234 5678 9012 3456"
                    value={card.number} maxLength={19}
                    onChange={e => { setCard(p=>({...p,number:fmtCard(e.target.value)})); setError(''); }}
                    style={{ fontFamily:'monospace', fontSize:15, letterSpacing:2 }} />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
                  <div>
                    <div className="section-label" style={{ marginBottom:6 }}>Expiry MM/YY</div>
                    <input type="text" inputMode="numeric" placeholder="MM/YY"
                      value={card.expiry} maxLength={5}
                      onChange={e => { setCard(p=>({...p,expiry:fmtExpiry(e.target.value)})); setError(''); }}
                      style={{ fontFamily:'monospace' }} />
                  </div>
                  <div>
                    <div className="section-label" style={{ marginBottom:6 }}>CVV</div>
                    <input type="password" inputMode="numeric" placeholder="•••"
                      value={card.cvv} maxLength={3}
                      onChange={e => { setCard(p=>({...p,cvv:e.target.value.replace(/\D/g,'').slice(0,3)})); setError(''); }}
                      style={{ fontFamily:'monospace', letterSpacing:4 }} />
                  </div>
                </div>
                <div style={{ marginBottom:20 }}>
                  <div className="section-label" style={{ marginBottom:6 }}>Cardholder Name</div>
                  <input type="text" placeholder="Name as on card"
                    value={card.name}
                    onChange={e => { setCard(p=>({...p,name:e.target.value.toUpperCase()})); setError(''); }}
                    style={{ textTransform:'uppercase', letterSpacing:1 }} />
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <button className="btn btn-ghost" style={{ flex:1 }} onClick={() => { setStep('select'); setError(''); }}>← Back</button>
                  <button className="btn btn-primary" style={{ flex:2, padding:13 }}
                    disabled={!cardValid() || loading} onClick={processPayment}>
                    {gateway === 'TAP' ? '🔵' : '💳'} Pay AED {total} →
                  </button>
                </div>
              </>
            )}

            {/* ── TAP QR / 3DS WAITING ── */}
            {step === 'tap-qr' && (
              <div style={{ textAlign:'center', padding:'10px 0' }}>
                <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
                  Scan this QR code with your phone to complete payment
                </div>
                {tapRedirectUrl && (
                  <div style={{ display:'inline-block', padding:8, background:'#fff', borderRadius:12, marginBottom:16 }}>
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(tapRedirectUrl)}`}
                      alt="Tap payment QR"
                      style={{ width:200, height:200, display:'block' }}
                    />
                  </div>
                )}
                <div style={{ fontSize:12, color:'var(--text3)', marginBottom:8 }}>
                  Or{' '}
                  <a href={tapRedirectUrl} target="_blank" rel="noreferrer" style={{ color:'#6080ff' }}>
                    open payment page →
                  </a>
                </div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginBottom:20, fontSize:13, color:'var(--text2)' }}>
                  <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:'var(--accent)', animation:'pulse 1.5s infinite' }} />
                  Waiting for payment confirmation...
                </div>
                <div style={{ fontSize:12, color:'var(--text3)', marginBottom:16 }}>
                  AED {total} · {order?.orderNumber}
                </div>
                <button className="btn btn-ghost" style={{ fontSize:13 }} onClick={handleCancelTap}>
                  Cancel payment
                </button>
              </div>
            )}

            {/* ── CASH COUNTER ── */}
            {step === 'cash-counter' && (
              <div style={{ textAlign:'center', padding:'10px 0' }}>
                <div style={{ fontSize:64, marginBottom:16 }}>🎫</div>
                <div style={{ fontSize:22, fontWeight:800, color:'var(--accent)', marginBottom:8 }}>Take this order to the counter</div>
                <div style={{ fontSize:14, color:'var(--text2)', marginBottom:20 }}>
                  Pay AED {total} in cash at the counter. Your order will start preparing once payment is received.
                </div>
                <div style={{ background:'var(--bg3)', border:'2px dashed var(--accent)', borderRadius:14, padding:'16px 24px', marginBottom:20, display:'inline-block' }}>
                  <div style={{ fontSize:11, color:'var(--text3)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Order Number</div>
                  <div style={{ fontSize:32, fontWeight:800, color:'var(--accent)', letterSpacing:3 }}>{order?.orderNumber}</div>
                  {order?.tableNo && <div style={{ fontSize:13, color:'var(--text2)', marginTop:6 }}>🪑 Table {order.tableNo}</div>}
                  <div style={{ fontSize:13, color:'var(--text2)', marginTop:4 }}>Amount due: <strong>AED {total}</strong></div>
                </div>
                <div>
                  <button className="btn btn-primary" style={{ padding:'12px 32px' }}
                    onClick={() => onSuccess({ success:true, gateway:'CASH', cash:true, cashPending:true })}>
                    ✓ Done
                  </button>
                </div>
              </div>
            )}

            {/* ── PROCESSING ── */}
            {step === 'processing' && (
              <div style={{ textAlign:'center', padding:'32px 20px' }}>
                <div style={{ fontSize:52, marginBottom:16 }}>⏳</div>
                <div style={{ fontSize:17, fontWeight:700, marginBottom:8 }}>Processing Payment...</div>
                <div style={{ fontSize:13, color:'var(--text2)' }}>
                  {gateway === 'TAP' ? 'Contacting Tap Payments...' : 'Please wait'}
                </div>
              </div>
            )}

            {/* ── SUCCESS ── */}
            {step === 'success' && (
              <div style={{ textAlign:'center', padding:'24px 20px' }}>
                <div style={{ fontSize:64, marginBottom:16 }}>✅</div>
                <div style={{ fontSize:22, fontWeight:800, color:'var(--green)', marginBottom:8 }}>Payment Confirmed!</div>
                <div style={{ fontSize:28, fontWeight:700, color:'var(--accent)', marginBottom:6 }}>AED {total}</div>
                <div style={{ fontSize:15, fontWeight:600, color:'var(--text)' }}>{order?.orderNumber}</div>
                <div style={{ fontSize:13, color:'var(--text2)', marginTop:6 }}>Order is being prepared...</div>
              </div>
            )}

            {/* ── DECLINED ── */}
            {step === 'declined' && (
              <div>
                <div style={{ textAlign:'center', padding:'16px 0 20px' }}>
                  <div style={{ fontSize:56, marginBottom:12 }}>❌</div>
                  <div style={{ fontSize:18, fontWeight:700, color:'var(--red)', marginBottom:8 }}>Payment Failed</div>
                  <div style={{ fontSize:14, color:'var(--text2)', marginBottom:12 }}>{error}</div>
                  {order?.orderNumber && (
                    <div style={{ padding:'10px 14px', background:'var(--bg3)', borderRadius:8, fontSize:13, color:'var(--text2)' }}>
                      Reference: <strong style={{ color:'var(--text)' }}>{order.orderNumber}</strong>
                    </div>
                  )}
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <button className="btn btn-ghost" style={{ flex:1 }} onClick={onClose}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex:2, padding:12 }}
                    onClick={() => { setStep(['CARD','TAP'].includes(gateway)?'card':'select'); setError(''); }}>
                    Try Again
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {showCashGate && (
        <CashGateModal
          orderTotal={total}
          onConfirm={handleCashGateConfirm}
          onCancel={() => setShowCashGate(false)}
        />
      )}
    </>
  );
}
